import React, { memo, useCallback, useMemo } from 'react';
import { PostCard } from './PostCard';
import { CastCard } from './CastCard';
import { AnnouncementCard } from './AnnouncementCard';
import { GrantAnnouncement } from './grants/GrantAnnouncement';
import { IsoCastAdPlacement } from './IsoCastAdPlacement';
import { AdPlacement } from './AdPlacement';
import { isAnnouncementItem, isIsoCastAd, isExternalAd } from '@/utils/feedWithAds';

interface LazyFeedRendererProps {
  items: any[];
  posts: any[];
  onLike: (id: string, type: 'post' | 'cast') => void;
  onComment: (id: string) => void;
}

export const LazyFeedRenderer = memo(({ items, posts, onLike, onComment }: LazyFeedRendererProps) => {
  const handleLike = useCallback((id: string, type: 'post' | 'cast') => {
    onLike(id, type);
  }, [onLike]);

  const handleComment = useCallback((id: string) => {
    onComment(id);
  }, [onComment]);

  const handleSignInRequired = useCallback(() => {
    // Trigger auth requirement
    window.dispatchEvent(new Event('requireAuth'));
  }, []);

  // Memoize rendered items to prevent unnecessary re-renders
  const renderedItems = useMemo(() => {
    return items.map((item, index) => {
      // Render announcement - unique key with type prefix
      if (isAnnouncementItem(item)) {
        return (
          <AnnouncementCard 
            key={`announcement-${item.id}-${index}`}
            announcement={item}
            onSignInRequired={handleSignInRequired}
          />
        );
      }

      // Render grant announcement - unique key with type prefix
      if (item.type === 'grant_announcement') {
        return (
          <GrantAnnouncement 
            key={`grant-${item.grant?.id || item.id}-${index}`}
            grant={item.grant}
            onSignInRequired={handleSignInRequired}
          />
        );
      }

      // Handle ISO Cast Ads - unique key with type prefix
      if (isIsoCastAd(item)) {
        return (
          <IsoCastAdPlacement
            key={`iso-ad-${item.cast?.id || item.id}-${index}`}
            cast={item.cast}
          />
        );
      }

      // Handle External Ads - unique key with type prefix
      if (isExternalAd(item)) {
        return (
          <AdPlacement
            key={`ext-ad-${item.id}-${index}`}
            adIndex={index}
          />
        );
      }

      // Handle regular posts - unique key with type prefix
      if (item.content || item.description) {
        const post = posts?.find(p => p.id === item.id) || item;
        return (
          <PostCard
            key={`post-${item.id}-${index}`}
            post={post}
            allPosts={posts || []}
            onLike={() => handleLike(item.id, 'post')}
            onComment={() => handleComment(item.id)}
          />
        );
      }

      // Handle casts - unique key with type prefix
      if (item.cast_content || item.response_type || item.iso_request || item.type === 'cast') {
        return (
          <CastCard
            key={`cast-${item.id}-${index}`}
            cast={item}
            onLike={() => handleLike(item.id, 'cast')}
            onComment={() => handleComment(item.id)}
          />
        );
      }

      return null;
    });
  }, [items, handleLike, handleComment, handleSignInRequired, posts]);

  return <>{renderedItems}</>;
});

LazyFeedRenderer.displayName = 'LazyFeedRenderer';